/**
 * FindingsGenerationStatus -- Loading and error states for AI finding generation
 */

import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui";
import { useGenerateFindings } from "@/packages/features-shared";
import type { Id } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

interface FindingsGenerationStatusProps {
  state: "loading" | "error";
  checklistItemId?: Id<"checklistItems">;
}

function SkeletonCard() {
  return (
    <div className="rounded-md border border-border/50 p-3 space-y-2 animate-pulse">
      <div className="flex items-center gap-2">
        <div className="h-2.5 w-2.5 rounded-full bg-muted/40" />
        <div className="h-4 w-16 rounded bg-muted/40" />
        <div className="h-4 w-32 rounded bg-muted/40" />
      </div>
      <div className="h-3 w-full rounded bg-muted/30" />
      <div className="h-3 w-3/4 rounded bg-muted/30" />
    </div>
  );
}

export function FindingsGenerationStatus({
  state,
  checklistItemId,
}: FindingsGenerationStatusProps) {
  const generateMutation = useGenerateFindings();
  const { t } = useTranslation("audits");

  if (state === "loading") {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 py-1">
          <Loader2 size={14} className="text-accent animate-spin" />
          <span className="text-sm text-muted-foreground">
            {t("checklist.findings.generating")}
          </span>
        </div>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  // Error state
  return (
    <div className="flex items-center gap-2 py-2">
      <AlertTriangle size={14} className="text-destructive shrink-0" />
      <span className="text-sm text-destructive">
        {t("checklist.findings.generationFailed")}
      </span>
      {checklistItemId && (
        <Button
          variant="secondary"
          size="sm"
          loading={generateMutation.isPending}
          onClick={() => generateMutation.mutateAsync({ checklistItemId })}
        >
          {t("checklist.findings.tryAgain")}
        </Button>
      )}
    </div>
  );
}
